import type { MarketTemplate, SubMarketGroupType } from '@ps/isp-web-services-types';
import type { Draft } from 'immer';
import produce from 'immer';
import { omit, pull } from 'lodash';

import type { MarketPollingData } from '@swf/sports/types/market';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';
import updateMarket from '@swf/sports/utils/updateMarket';

import { defaultState } from './defaultState';
import type { EventPageTemplateView, MarketGroupsArray } from './types';

type State = typeof defaultState;

export const reducers = {
  clear: (): State => produce(defaultState, (draft) => draft),
  eventPageRequested: (state: State, pageChanged: boolean = false): State => ({
    ...state,
    isLoading: true,
    error: undefined,
    errorCode: undefined,
    events: pageChanged ? {} : state.events,
    competitions: pageChanged ? {} : state.competitions,
    markets: pageChanged ? {} : state.markets,
    eventTypes: pageChanged ? {} : state.eventTypes,
    hasPreloaded: pageChanged ? false : state.hasPreloaded,
    selectedSubMarketGroupType: pageChanged ? undefined : state.selectedSubMarketGroupType
  }),
  eventPageLoadSuccess: (state: State, payload?: EventPageTemplateView): State => ({
    ...state,
    ...payload,
    isLoading: false,
    selectedSubMarketGroupType: payload?.marketsFeature
      ? payload.marketsFeature
          .flatMap((marketGroup) => marketGroup.groupedTemplates?.flat() ?? [])
          .find((group) => group.type === state.selectedSubMarketGroupType)?.type
      : undefined,
    hasPreloaded: true
  }),
  eventPageLoadFailure: (state: State, payload: number): State => ({
    ...state,
    isLoading: false,
    hasPreloaded: true,
    error: true,
    errorCode: payload
  }),
  marketsUpdated: (state: State, { data: marketUpdates, closed }: MarketPollingData) =>
    typeof state.error === 'undefined'
      ? produce(state, (draft) => {
          // Remove any closed markets from the store

          if (closed.length) {
            draft.markets = omit(draft.markets, closed);

            draft.marketsFeature = draft.marketsFeature.filter((group) => {
              const filterAndUpdateTemplates = (
                templates: MarketTemplate[],
                closedMarketIds: string[]
              ) =>
                templates.filter((template) => {
                  if (isCompoundMarketTemplate(template)) {
                    template.marketIds = pull(template.marketIds, ...closedMarketIds);
                    return Boolean(template.marketIds.length);
                  }
                  return !closedMarketIds.includes(template.marketId);
                });

              group.templates = filterAndUpdateTemplates(group.templates, closed);

              group.groupedTemplates = group.groupedTemplates?.filter((groupTemplate) => {
                groupTemplate.templates = filterAndUpdateTemplates(groupTemplate.templates, closed);
                return groupTemplate.templates.length > 0;
              });

              // TODO: https://jira.pyrsoftware.ca/jira/browse/GSFE-3646
              if ('popularBetBuilders' in group && group.popularBetBuilders) {
                group.popularBetBuilders = group.popularBetBuilders.filter((pbb) =>
                  pbb.runnersIds.every(({ marketId }) => !closed.includes(marketId))
                );
              }

              return Boolean(group.templates.length);
            }) as Draft<MarketGroupsArray>;
          }

          // Update the markets with new data
          Object.entries(marketUpdates).forEach(([marketUpdateId, marketUpdate]) => {
            const market = draft.markets[marketUpdateId];

            if (market && marketUpdate) updateMarket(market, marketUpdate);
          });

          return draft;
        })
      : state,
  setSelectedActionTile: (state: State, type: SubMarketGroupType | undefined): State => ({
    ...state,
    selectedSubMarketGroupType: type
  })
};
